// src/pages/MyOrdersPage.js
import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firestore';
import { useUser } from '../context/userContext';
import { useCart } from '../context/cartContext';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const MyOrdersPage = () => {
  const { id: userId, telegramUser } = useUser();
  const { addToCart } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

const statusTranslations = {
  pending: 'Pending',
  paid: 'Paid',
  shipped: 'Shipped',
  done: 'Completed'
};

const statusStyles = {
  pending: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700',
  shipped: 'bg-blue-100 text-blue-700',
  done: 'bg-gray-100 text-gray-700',
};

  // Telegram Back Button
  useEffect(() => {
    const tg = window.Telegram.WebApp;
    tg.BackButton.show();
    tg.BackButton.onClick(() => navigate(-1));
    return () => {
      tg.BackButton.hide();
      tg.BackButton.offClick();
    };
  }, [navigate]);

useEffect(() => {
  const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));

  const unsubscribe = onSnapshot(q, snapshot => {
    const userOrders = snapshot.docs
      .map(doc => {
        const data = doc.data();

        // Безопасно обработаем createdAt
        let createdAtDate = data.createdAt;
        if (createdAtDate?.toDate) createdAtDate = createdAtDate.toDate();
        else if (!(createdAtDate instanceof Date)) createdAtDate = new Date();

        return {
          id: doc.id,
          orderId: data.orderId || doc.id,
          userId: data.userId || null,
          status: data.status || 'pending',
          items: data.items || [],
          total: data.total || 0,
          createdAt: createdAtDate
        };
      })
      // Показываем только заказы текущего пользователя
      .filter(order => order.userId === (userId || telegramUser?.id?.toString()));

    setOrders(userOrders);
    setLoading(false);
  }, error => {
    console.error('Error fetching orders:', error);
    setLoading(false);
  });

  return () => unsubscribe();
}, [userId, telegramUser]);


  const handleRepeatOrder = (items) => {
    items.forEach(item => {
      addToCart({
        id: item.id,
        title: item.title,
        price: item.price,
        image: item.image,
        quantity: item.quantity || 1,
      });
    });
    navigate('/cart');
  };

  if (loading) return <p className="p-4">Загрузка заказов..</p>;

  if (!orders.length) return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Ваши заказы</h2>
      <p className="text-gray-500">У вас пока нет оформленных заказов.</p>
    </div>
  );

  return (
    <div className="bg-[#EBF0F3] min-h-screen p-4">
      <h2 className="text-2xl font-bold mb-6">Ваши заказы</h2>
      <div className="space-y-6">
        {orders.map(order => (
          <div key={order.id} className="bg-white p-4 rounded-xl shadow">
            <div className="flex justify-between mb-2">
              <span>№ {order.orderId}</span>
              <span>{moment(order.createdAt).format('DD.MM.YYYY, HH:mm')}</span>
            </div>

            <span className={`text-xs px-2 py-1 rounded font-medium ${statusStyles[order.status] || statusStyles.pending}`}>
              {statusTranslations[order.status] || 'Pending'}
            </span>

            <div className="flex items-center gap-2 overflow-hidden mt-2 mb-1">
              {order.items.slice(0, 3).map((item, idx) => (
                <img key={idx} src={item.image} alt={item.title} className="w-10 h-10 rounded object-cover border" />
              ))}
              {order.items.length > 3 && <span className="text-xs text-gray-500">+{order.items.length - 3} ещё товары</span>}
            </div>

            <div className="mt-2 space-y-1 text-sm text-gray-700 max-h-[80px] overflow-y-auto">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="truncate w-1/2">{item.title}</span>
                  <span className="text-right w-1/2">{item.price}₽ × {item.quantity}</span>
                </div>
              ))}
            </div>

            <div className="mt-2 font-bold text-right text-cyan-700 text-sm">Итого: {order.total}₽</div>

            <button
              onClick={() => handleRepeatOrder(order.items)}
              className="mt-3 w-fit bg-yellow-100 text-yellow-700 text-xs font-medium px-3 py-1 rounded hover:bg-yellow-200 transition block ml-auto"
            >
              Повторить заказ
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyOrdersPage;
